import * as React from 'react'

import { Box, Table, Text } from '@factorialco/deprecated-design-system'
import { AttendanceCustomTimeRangeDayEnum } from 'generated/resources/graphql'
import type { SettingPageCustomTimeRangeFragment } from 'generated/resources/graphql'

import { useCustomTimeRange } from 'modules/attendance/hooks/useCustomTimeRange'
import { useAttendanceSettingWorkCategories } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'
import CustomTimeRangeUtils from 'modules/attendance/lib/utils/CustomTimeRangeUtils'

import Block from './Block'
import Cell from './Cell'

type Column = AttendanceCustomTimeRangeDayEnum | 'specific_date'

interface Props {
  dateRangesByDate?: Record<string, SettingPageCustomTimeRangeFragment[]>
  sortedRangeDates?: string[]
  showSpecificDatesColumn?: boolean
}

const TableBody = ({
  dateRangesByDate = {},
  sortedRangeDates = [],
  showSpecificDatesColumn = false,
}: Props) => {
  const { dayEnums } = useCustomTimeRange()
  const { customTimeRanges } = useAttendanceSettingWorkCategories()

  const columns: Column[] = [
    ...(showSpecificDatesColumn ? ['specific_date' as const] : []),
    ...dayEnums,
  ]

  const isSpecificDate = (column: Column): column is 'specific_date' => column === 'specific_date'

  const getColumnKey = (column: Column): string => column

  const getAllDateRanges = (
    filterByType?: 'base' | 'custom'
  ): SettingPageCustomTimeRangeFragment[] => {
    const allDateRanges = sortedRangeDates.flatMap((date) => dateRangesByDate[date] ?? [])

    if (filterByType === 'base') {
      return CustomTimeRangeUtils.baseTypeTimeRanges(allDateRanges)
    }
    if (filterByType === 'custom') {
      return CustomTimeRangeUtils.customTypeTimeRanges(allDateRanges)
    }

    return allDateRanges
  }

  const timeRangesByStartTime = CustomTimeRangeUtils.timeRangesByStartTime(customTimeRanges)
  const sortedStartTimes = CustomTimeRangeUtils.sortedStartTimes(timeRangesByStartTime)
  const hasCustomTypeTimeRanges = CustomTimeRangeUtils.hasCustomTypeTimeRanges(customTimeRanges)
  const allBaseRanges = CustomTimeRangeUtils.baseTypeTimeRanges(customTimeRanges)
  const hasBaseTypeTimeRanges = allBaseRanges.length > 0 || getAllDateRanges('base').length > 0
  const hasTimeRanges =
    CustomTimeRangeUtils.hasTimeRanges(customTimeRanges) || sortedRangeDates.length > 0

  const renderEmptyRow = () => (
    <Table.Row background="grey100">
      {columns.map((column) => (
        <Table.Cell key={`empty_${getColumnKey(column)}`} noBorder>
          {isSpecificDate(column) ? (
            <Cell timeRanges={[]} dayType={undefined} base showNotDefined isSpecificDateMode />
          ) : (
            <Cell timeRanges={[]} dayType={column} base showNotDefined />
          )}
        </Table.Cell>
      ))}
    </Table.Row>
  )

  if (!hasTimeRanges) {
    return <Table.Body>{renderEmptyRow()}</Table.Body>
  }

  const renderBaseTypeRows = () => {
    const allTimeTypesByType: Record<string, SettingPageCustomTimeRangeFragment[]> =
      CustomTimeRangeUtils.getAllTimeRangesByType(allBaseRanges)

    const allDateBaseRanges = getAllDateRanges('base')
    const dateRangesByTimeType: Record<string, SettingPageCustomTimeRangeFragment[]> = {
      regular: allDateBaseRanges.filter((r) => r.timeType === 'regular'),
      flexible: allDateBaseRanges.filter((r) => r.timeType === 'any_time'),
      overtime: allDateBaseRanges.filter((r) => r.timeType === 'overtime'),
    }

    const timeTypesWithRanges = new Set([
      ...Object.entries(allTimeTypesByType)
        .filter(([, ranges]) => (ranges || []).length > 0)
        .map(([type]) => type),
      ...Object.keys(dateRangesByTimeType).filter(
        (type) => (dateRangesByTimeType[type] || []).length > 0
      ),
    ])

    return Array.from(timeTypesWithRanges).map((timeType) => {
      const rangesByType = allTimeTypesByType[timeType] || []
      const dateRangesForTimeType = dateRangesByTimeType[timeType] || []

      const dayRangesForTimeType = rangesByType.filter((range) =>
        CustomTimeRangeUtils.withDay(range)
      )

      const hasAnyRanges = dayRangesForTimeType.length > 0 || dateRangesForTimeType.length > 0

      if (!hasAnyRanges) return null

      return (
        <Table.Row key={timeType} background="grey100">
          {columns.map((column) => {
            if (isSpecificDate(column)) {
              // For specific date column: show ALL base ranges for ALL dates, filtered by time type
              // Multiple dates can coexist because they don't conflict
              // Map timeType key to actual timeType value
              const actualTimeType = timeType === 'flexible' ? 'any_time' : timeType
              const rangesForThisTimeType = dateRangesForTimeType

              return (
                <Table.Cell
                  key={`${timeType}_${getColumnKey(column)}`}
                  noBorder
                  verticalAlign="top"
                >
                  {rangesForThisTimeType.length > 0 ? (
                    <Box gap="s12" padding="s12" minWidth="s240">
                      {sortedRangeDates.map((date) => {
                        const dateRanges = dateRangesByDate[date] ?? []
                        // Map timeType key to actual timeType value for filtering
                        const rangesForDateAndTimeType = dateRanges.filter(
                          (range) =>
                            CustomTimeRangeUtils.isBase(range) && range.timeType === actualTimeType
                        )
                        return rangesForDateAndTimeType.map((range) => (
                          <Block key={`${date}_${range.id}`} timeRanges={[range]} base />
                        ))
                      })}
                    </Box>
                  ) : (
                    <Cell
                      timeRanges={[]}
                      dayType={undefined}
                      base
                      showNotDefined
                      isSpecificDateMode
                    />
                  )}
                </Table.Cell>
              )
            } else {
              // For day columns: use day-based ranges for this time type
              const ranges = CustomTimeRangeUtils.rangesInSpecificBaseDay(
                dayRangesForTimeType,
                column
              )
              return (
                <Table.Cell
                  key={`${timeType}_${getColumnKey(column)}`}
                  noBorder
                  verticalAlign="top"
                >
                  <Cell
                    timeRanges={ranges}
                    dayType={column}
                    base
                    showNotDefined={
                      !CustomTimeRangeUtils.hasRangesInSpecificDay(customTimeRanges, column)
                    }
                  />
                </Table.Cell>
              )
            }
          })}
        </Table.Row>
      )
    })
  }

  const renderCustomTypeRows = () =>
    sortedStartTimes.map((startTime, index) => (
      <Table.Row key={startTime} background="grey100">
        {columns.map((column) => {
          if (isSpecificDate(column)) {
            // For specific date column: show ALL custom ranges for ALL dates that start at this time
            const allDateRanges = getAllDateRanges('custom')
            const rangesForThisStartTime = allDateRanges.filter(
              (range) => range.startTime === startTime
            )

            return (
              <Table.Cell key={`${startTime}_${getColumnKey(column)}`} noBorder verticalAlign="top">
                {rangesForThisStartTime.length > 0 ? (
                  <Box gap="s12" padding="s12" minWidth="s240">
                    {sortedRangeDates.map((date) => {
                      const dateRanges = dateRangesByDate[date] ?? []
                      const rangesForDateAndStartTime = dateRanges.filter(
                        (range) =>
                          CustomTimeRangeUtils.isCustom(range) && range.startTime === startTime
                      )
                      return rangesForDateAndStartTime.map((range) => (
                        <Block key={`${date}_${range.id}`} timeRanges={[range]} base={false} />
                      ))
                    })}
                  </Box>
                ) : (
                  <Box paddingTop={!hasBaseTypeTimeRanges && index === 0 ? 's12' : undefined}>
                    <Cell
                      timeRanges={[]}
                      dayType={undefined}
                      base={false}
                      showNotDefined={!hasBaseTypeTimeRanges && index === 0}
                      isSpecificDateMode
                    />
                  </Box>
                )}
              </Table.Cell>
            )
          } else {
            // For day columns: existing logic (filtered by day and start time)
            const ranges = CustomTimeRangeUtils.rangesInSpecificCustomDay(
              timeRangesByStartTime[startTime] ?? [],
              column
            )
            return (
              <Table.Cell key={`time_slot_${getColumnKey(column)}`} noBorder verticalAlign="top">
                <Box paddingTop={!hasBaseTypeTimeRanges && index === 0 ? 's12' : undefined}>
                  <Cell
                    timeRanges={ranges}
                    dayType={column}
                    showNotDefined={
                      !hasBaseTypeTimeRanges &&
                      index === 0 &&
                      !CustomTimeRangeUtils.hasRangesInSpecificDay(customTimeRanges, column)
                    }
                  />
                </Box>
              </Table.Cell>
            )
          }
        })}
      </Table.Row>
    ))

  return (
    <Table.Body>
      {hasBaseTypeTimeRanges && renderBaseTypeRows()}
      {hasCustomTypeTimeRanges && renderCustomTypeRows()}
    </Table.Body>
  )
}

export default TableBody
